import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import {
    fetchUsers,
} from '../../features/users/usersSlice'

import './UserManagementDashboard.css'
import EmptyStateImage from '@/assets/images/user_dashboard_empty_state.webp'
import CButton from '../../components/UI/CButton'
import CDataGrid from '../../components/UI/CDataGrid'
import CDialog from '../../components/UI/CDialog'
import UserForm from './components/UserForm'
import UploadUsersForm from './components/UploadUsersForm'
import UploadFundsCSVForm from './components/UploadFundsCSVForm'
import IssueFundsForm from './components/IssueFundsForm'
import PurchaseFundsForm from '../fund-management/components/PurchaseFundsForm'
import Icon from '../../components/UI/Icon'

const UserManagementDashboard = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const {
        users,
        loading,
        error,
        totalUsers
    } = useSelector((state) => state.users)

    const [userDialogOpen, setUserDialogOpen] = useState(false)
    const [editingUser, setEditingUser] = useState(null)

    const [uploadUsersDialogOpen, setUploadUsersDialogOpen] = useState(false)
    const [uploadFundsDialogOpen, setUploadFundsDialogOpen] = useState(false)

    const [issueFundsDialogOpen, setIssueFundsDialogOpen] = useState(false)
    const [showPurchaseForm, setShowPurchaseForm] = useState(false)
    const [selectedUsers, setSelectedUsers] = useState([])
    const [gridKey, setGridKey] = useState(0)
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })

    useEffect(() => {
        dispatch(fetchUsers({
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize
        }))
    }, [dispatch, paginationModel])

    // Check for navigation state to open dialogs
    useEffect(() => {
        if (location.state?.openDialog) {
            switch (location.state.openDialog) {
                case 'addUser':
                    setUserDialogOpen(true)
                    break
                case 'uploadUsers':
                    setUploadUsersDialogOpen(true)
                    break
                default:
                    break
            }
            // Clear the state after opening dialog
            window.history.replaceState({}, document.title)
        }
    }, [location])

    // User Add/Edit Dialog Handlers
    const handleAddUserClick = () => {
        setEditingUser(null)
        setUserDialogOpen(true)
    }

    const handleEditUserClick = (user) => {
        setEditingUser(user)
        setUserDialogOpen(true)
    }

    const handleUserDialogClose = () => {
        setUserDialogOpen(false)
        setEditingUser(null)
    }

    const handleUserFormSuccess = () => {
        // Refresh users list with current pagination
        dispatch(fetchUsers({
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize
        }))
    }

    // Upload Users CSV Handlers
    const handleUploadUsersClick = () => {
        setUploadUsersDialogOpen(true)
    }

    const handleUploadUsersDialogClose = () => {
        setUploadUsersDialogOpen(false)
    }

    // Upload Funds CSV Handlers
    const handleUploadFundsClick = () => {
        setUploadFundsDialogOpen(true)
    }

    const handleUploadFundsDialogClose = () => {
        setUploadFundsDialogOpen(false)
    }

    // Issue Funds to Selected Users Handlers
    const handleIssueFundsClick = () => {
        setShowPurchaseForm(false)
        setIssueFundsDialogOpen(true)
    }

    const handleIssueFundsDialogClose = () => {
        setIssueFundsDialogOpen(false)
        setShowPurchaseForm(false)
    }

    const handleIssueFundsSuccess = () => {
        // Clear selection and refresh grid after successful issue
        setSelectedUsers([])
        setGridKey(prev => prev + 1)
    }

    const handleTopUpFundsClick = () => {
        setShowPurchaseForm(true)
    }

    const handlePurchaseFundsCancel = () => {
        // Go back to issue funds form when canceling purchase
        setShowPurchaseForm(false)
    }

    const handlePurchaseFundsSuccess = () => {
        // Switch back to issue funds form after successful purchase
        setShowPurchaseForm(false)
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
        }).format(amount)
    }

    const columns = [
        {
            field: 'firstnames',
            headerName: 'Name',
            flex: 1.2,
            minWidth: 50,
        },
            {
            field: 'surname',
            headerName: 'Surname',
            flex: 1.2,
            minWidth: 50,
        },
        {
            field: 'idnumber',
            headerName: 'ID Number',
            flex: 1.2,
            minWidth: 120,
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1.5,
            minWidth: 180,
        },
        {
            field: 'cell',
            headerName: 'Mobile Number',
            flex: 1.2,
            minWidth: 130,
        },
        {
            field: 'currencybalance',
            headerName: 'Balance',
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                return (
                    <span className="balance-cell header-4">
                        {formatCurrency(params.value || 0)}
                    </span>
                )
            },
        },
        {
            field: 'isactive',
            headerName: 'Status',
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => {
                const status = params.value ? 'Active' : 'Inactive'
                const statusClass = `status-badge status-${status.toLowerCase()}`
                return (
                    <span className={statusClass}>
                        {status}
                    </span>
                )
            },
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.8,
            minWidth: 80,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div className="actions-cell">
                        <button
                            className="action-button edit"
                            onClick={() => handleEditUserClick(params.row)}
                            title="Edit user"
                        >
                            <Icon name="edit" />
                        </button>
                    </div>
                )
            },
        },
    ]

    return (
        <div className="user-management-dashboard">
            {/* Header Section */}
            <div className="dashboard-header">
                <span className="dashboard-title header-2">User Management</span>
                <div className="header-actions">
                    <CButton
                        label="Add user"
                        onClick={handleAddUserClick}
                        variant="outlined"
                        color="primary"
                    />
                    <CButton
                        label="Upload users"
                        onClick={handleUploadUsersClick}
                        variant="outlined"
                        color="primary"
                        loading={loading.uploadUsers}
                    />
                    <CButton
                        label="Issue funds"
                        leftIcon={"credit-card-sync"}
                        onClick={handleUploadFundsClick}
                        variant="contained"
                        color="primary"
                        loading={loading.uploadFunds}
                    />
                </div>
            </div>

            <div className="divider"></div>

            {/* Selected Users Actions */}
            {selectedUsers.length > 0 && (
                <div className="selected-users-bar">
                    <span className="selected-count header-4">
                        {selectedUsers.length} user(s) selected
                    </span>
                    <CButton
                        label="Issue Funds"
                        leftIcon={"credit-card-sync"}
                        onClick={handleIssueFundsClick}
                        variant="contained"
                        color="primary"
                        size="small"
                    />
                </div>
            )}

            {/* Users DataGrid */}
            <div className="users-grid-container">
                <CDataGrid
                    key={gridKey}
                    rows={users}
                    columns={columns}
                    rowCount={totalUsers}
                    loading={loading.fetch}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    initialPageSize={10}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    getRowId={(row) => row.userid || row.personid}
                    checkboxSelection
                    onRowSelectionModelChange={(newSelection) => {
                        setSelectedUsers(newSelection)
                    }}
                    emptyStateImage={EmptyStateImage}
                    emptyStateTitle="No registered users"
                    emptyStateDescription="You'll need to add users before funds can be issued."
                    emptyStateButtonLabel="Add your first user"
                    emptyStateOnClick={handleAddUserClick}
                />
            </div>

            {/* Add/Edit User Dialog */}
            <CDialog
                open={userDialogOpen}
                onClose={handleUserDialogClose}
                title={editingUser ? 'Edit User' : 'Add User'}
                titleIcon={"multiple-actions-add"}
                maxWidth="sm"
                fullWidth
            >
                <UserForm
                    user={editingUser}
                    onClose={handleUserDialogClose}
                    onSuccess={handleUserFormSuccess}
                />
            </CDialog>

            {/* Upload Users CSV Dialog */}
            <CDialog
                open={uploadUsersDialogOpen}
                onClose={handleUploadUsersDialogClose}
                title="Upload users"
                titleIcon={"single-neutral-actions-upload"}
                maxWidth="sm"
                fullWidth
            >
                <UploadUsersForm
                    onClose={handleUploadUsersDialogClose}
                />
            </CDialog>

            {/* Upload Funds CSV Dialog */}
            <CDialog
                open={uploadFundsDialogOpen}
                onClose={handleUploadFundsDialogClose}
                title="Issue funds"
                titleIcon={"single-neutral-actions-upload"}
                maxWidth="sm"
                fullWidth
            >
                <UploadFundsCSVForm
                    onClose={handleUploadFundsDialogClose}
                />
            </CDialog>

            {/* Issue Funds Dialog */}
            <CDialog
                open={issueFundsDialogOpen}
                onClose={handleIssueFundsDialogClose}
                title={showPurchaseForm ? "Purchase funds" : "Issue funds"}
                titleIcon={"credit-card-sync"}
                maxWidth="sm"
                fullWidth
            >
                {showPurchaseForm ? (
                    <PurchaseFundsForm
                        onClose={handlePurchaseFundsCancel}
                        onSuccess={handlePurchaseFundsSuccess}
                        currency="USD"
                    />
                ) : (
                    <IssueFundsForm
                        selectedUsers={selectedUsers}
                        onClose={handleIssueFundsDialogClose}
                        onSuccess={handleIssueFundsSuccess}
                        onTopUpFunds={handleTopUpFundsClick}
                        currency="ZAR"
                    />
                )}
            </CDialog>
        </div>
    )
}

export default UserManagementDashboard
